import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

export function ContactForm() {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    website_url: '', // Honeypot
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          website_url: '',
        });
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
    }
  };

  return (
    <AnimatePresence mode="wait">
      {status === 'success' ? (
        <motion.div 
          key="success"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-emerald-50 border border-emerald-100 p-8 rounded-3xl text-center space-y-4"
        >
          <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h3 className="text-2xl font-bold text-emerald-900">Tak for din besked!</h3>
          <p className="text-emerald-700">
            Vi har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="text-emerald-600 font-semibold hover:underline"
          >
            Send en ny besked
          </button>
        </motion.div>
      ) : (
        <motion.form 
          key="form"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onSubmit={handleSubmit} 
          className="space-y-6"
        >
          {/* Honeypot - Hidden from humans */}
          <div className="sr-only absolute -z-10 opacity-0">
            <label htmlFor="website_url">Website URL</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium text-emerald-900 ml-1">
                Navn
              </label>
              <input
                required
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Dit fulde navn"
                className="w-full px-5 py-4 rounded-2xl bg-white border border-emerald-100 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all placeholder:text-emerald-300"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-emerald-900 ml-1">
                Email
              </label>
              <input
                required
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className="w-full px-5 py-4 rounded-2xl bg-white border border-emerald-100 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all placeholder:text-emerald-300"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="phone" className="text-sm font-medium text-emerald-900 ml-1">
                Telefon
              </label>
              <input
                required
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Dit telefonnummer"
                className="w-full px-5 py-4 rounded-2xl bg-white border border-emerald-100 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all placeholder:text-emerald-300"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="subject" className="text-sm font-medium text-emerald-900 ml-1">
                Emne
              </label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-5 py-4 rounded-2xl bg-white border border-emerald-100 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-emerald-900"
              >
                <option value="">Vælg emne</option>
                <option value="Ejendomsservice">Ejendomsservice</option>
                <option value="Græsslåning">Græsslåning</option>
                <option value="Flishugning">Flishugning</option>
                <option value="Havearbejde">Havearbejde</option>
                <option value="Andet">Andet</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="message" className="text-sm font-medium text-emerald-900 ml-1">
              Besked
            </label>
            <textarea
              required
              id="message"
              name="message"
              rows="5"
              value={formData.message}
              onChange={handleChange}
              placeholder="Fortæl os lidt om din opgave..."
              className="w-full px-5 py-4 rounded-2xl bg-white border border-emerald-100 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all placeholder:text-emerald-300 resize-none"
            ></textarea>
          </div>

          {status === 'error' && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-xl border border-red-100">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm font-medium">Der skete en fejl. Prøv venligst igen senere.</p>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'loading'}
            className={cn(
              "w-full flex items-center justify-center gap-2 py-5 rounded-2xl font-bold text-lg transition-all shadow-lg hover:shadow-xl active:scale-[0.98]",
              status === 'loading' 
                ? "bg-emerald-400 cursor-not-allowed" 
                : "bg-red-600 text-white hover:bg-red-700 shadow-red-600/20"
            )}
          >
            {status === 'loading' ? (
              <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                Send besked
                <Send className="w-5 h-5" />
              </>
            )}
          </button>
        </motion.form>
      )}
    </AnimatePresence>
  );
}
